import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress } from "@mui/material";

/** How often the case is re-read while a web-service call runs, and for how long. */
export const WEBSERVICE_POLL_INTERVAL_MS = 3000;
export const WEBSERVICE_POLL_LIMIT_MS = 2 * 60 * 1000;

interface WorkflowWebServiceWaitNoticeProps {
  /** Re-reads the case; resolves false if that failed (useApi has already raised its alert). */
  onRefresh: () => Promise<boolean>;
}

/**
 * Decision 6.4: a web-service step runs in the background (Epic 80425's worker), so the case waits
 * on it for a few seconds to about a minute. The screen re-reads the case every few seconds until
 * the engine moves it on, which unmounts this notice. It stops after two minutes, or at the first
 * failed re-read (each failure would raise another alert), and offers Reload instead.
 */
const WorkflowWebServiceWaitNotice: React.FC<WorkflowWebServiceWaitNoticeProps> = ({ onRefresh }) => {
  const [polling, setPolling] = useState(true);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (!polling) return undefined;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    const poll = () => {
      timer = setTimeout(async () => {
        const reloaded = await onRefreshRef.current();
        if (!active) return;
        if (!reloaded || Date.now() - startedAt >= WEBSERVICE_POLL_LIMIT_MS) setPolling(false);
        else poll();
      }, WEBSERVICE_POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [polling]);

  if (polling) {
    return (
      <Alert severity="info" className="workflow-run__alert--flush" icon={<CircularProgress size={18} aria-hidden="true" />}>
        <span role="status">Running automatically…</span> This step calls another system in the
        background; the case moves on by itself. If that system doesn&apos;t respond, this can take up to
        about 25 minutes.
      </Alert>
    );
  }

  return (
    <>
      <Alert severity="warning" className="workflow-run__alert--flush">
        This step is still running in the background. Reload to check on it.
      </Alert>
      <Box className="workflow-run__actions workflow-run__actions--end">
        <Button variant="outlined" color="secondary" onClick={() => setPolling(true)}>
          Reload
        </Button>
      </Box>
    </>
  );
};

export default WorkflowWebServiceWaitNotice;
